'use client';

import { Box } from '@mui/material';

import { TennisScoreDisplay } from '@/components/specific';
import { EventResponse } from '@/types';
import { isTennisGame } from '@/utils';
import TeamInfo from '../TeamInfo';

interface TeamInfoColumnProps {
  isStartingSoon?: boolean;
  event?: EventResponse;
  /** First team data (usually Home) */
  team1: {
    abbr: string;
    name: string;
    record: string;
    color: string;
    liveScore?: string;
    logo?: string;
    isWinner?: boolean;
  };
  /** Second team data (usually Away) */
  team2: {
    abbr: string;
    name: string;
    record: string;
    color: string;
    liveScore?: string;
    logo?: string;
    isWinner?: boolean;
  };
  /** Whether the game is currently live */
  isLive?: boolean;
  isPreviousMarket?: boolean;
}

/**
 * TeamInfoColumn renders two `TeamInfo` blocks stacked vertically.
 * It is primarily used within `SportsGameCard` to display the matching competitors.
 */
export const TeamInfoColumn = ({
  isStartingSoon = false,
  event,
  team1,
  team2,
  isLive,
  isPreviousMarket,
}: TeamInfoColumnProps) => {
  if (!team1 && !team2) return null;

  const isTennis = isTennisGame(event);

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 200,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: { xs: 'space-between', md: 'flex-start' },
          gap: 2,
          minWidth: 0,
        }}
      >
        <TeamInfo
          abbr={team1?.abbr || ''}
          name={team1?.name || ''}
          record={team1?.record || ''}
          color={team1?.color}
          liveScore={team1?.liveScore}
          logo={team1?.logo}
          isWinner={team1?.isWinner}
          isLive={isLive}
          isPreviousMarket={isPreviousMarket}
          hideLiveScore={isTennis || isStartingSoon}
        />
        {isTennis && event.score && <TennisScoreDisplay scores={event.score} isPlayer1={true} />}
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: { xs: 'space-between', md: 'flex-start' },
          gap: 2,
          minWidth: 0,
        }}
      >
        <TeamInfo
          abbr={team2?.abbr || ''}
          name={team2?.name || ''}
          record={team2?.record || ''}
          color={team2?.color}
          liveScore={team2?.liveScore}
          logo={team2?.logo}
          isWinner={team2?.isWinner}
          isLive={isLive}
          isPreviousMarket={isPreviousMarket}
          hideLiveScore={isTennis || isStartingSoon}
        />
        {isTennis && event.score && <TennisScoreDisplay scores={event.score} isPlayer1={false} />}
      </Box>
    </Box>
  );
};
